import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import FormControl from "@mui/material/FormControl";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddBoxOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

import { ComponentCategory, PERFUME_BOTTLE_VOLUME, type PerfumeIngredient } from "@/models";
import {
  type PerfumeEditor,
  addComponentWithDialog,
  duplicateComponent,
  deleteComponent,
  editComponent,
  setPerfumeProperty,
  openComponentIngredientDialog,
  closeComponentIngredientDialog,
  openComponentVolumeDialog,
  closeComponentVolumeDialog,
} from "@/services/perfume-editor";
import type { EditComponentViewModel } from "@/services/perfume-editor/EditPerfumeViewModel";
import { PerfumeCategoryTitle } from "./PerfumeCategoryTitle";
import { IngredientDialog } from "./IngredientDialog";
import {
  IngredientSuggestionDialog,
  hasIngredientsToSuggest,
  isBottleFull,
} from "./IngredientSuggestionDialog";
import { IngredientPicture } from "./IngredientPicture";
import { useIngredientDiplayName } from "@/services";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

export function PerfumeComposer({ editor, hide }: { editor: PerfumeEditor; hide: boolean }) {
  const perfumeViewModel = editor.perfumeViewModel;

  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: hide ? "none" : undefined,
      }}
    >
      <FormControl>
        <TextField
          required
          label={t("perfumeName")}
          value={perfumeViewModel.name}
          variant="standard"
          onChange={(e) =>
            setPerfumeProperty(editor, {
              newName: e.target.value,
            })
          }
        />
      </FormControl>

      <Stack
        sx={{
          gap: 2,
          my: 4,
        }}
      >
        <ListOfCompositions editor={editor} category={ComponentCategory.NOTE_DE_TETE} />
        {/* <Divider /> */}
        <ListOfCompositions editor={editor} category={ComponentCategory.NOTE_DE_COEUR} />
        {/* <Divider /> */}
        <ListOfCompositions editor={editor} category={ComponentCategory.NOTE_DE_FOND} />
        <Divider />
        <Box sx={{ color: editor.totalVolume > PERFUME_BOTTLE_VOLUME ? "error.main" : undefined }}>
          {t("totalVolume")}: {editor.totalVolume}ml
          {editor.totalVolume > PERFUME_BOTTLE_VOLUME &&
            ` (${t("totalVolumeOverBottle", { bottle: PERFUME_BOTTLE_VOLUME })})`}
        </Box>
        <FormControl
          fullWidth
          sx={{
            my: 2,
          }}
        >
          <TextField
            label={t("comment")}
            value={editor.perfumeViewModel.comment}
            onChange={(e) =>
              setPerfumeProperty(editor, {
                newComment: e.target.value,
              })
            }
            variant="standard"
            multiline
            minRows={2}
          />
        </FormControl>
      </Stack>
    </Box>
  );
}

function groupComponents(
  components: EditComponentViewModel[],
): { key: string; components: EditComponentViewModel[] }[] {
  const map = new Map<string, EditComponentViewModel[]>();
  for (const c of components) {
    const key = c.ingredient?.name ?? `__null__${c.componentId}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(c);
  }
  return Array.from(map.entries()).map(([key, comps]) => ({ key, components: comps }));
}

function ListOfCompositions({
  editor,
  category,
}: {
  editor: PerfumeEditor;
  category: ComponentCategory;
}) {
  const components = editor.perfumeViewModel.components.filter((c) => c.category === category);
  const groups = groupComponents(components);
  const { t } = useTranslation();
  const [suggestionDialogOpen, setSuggestionDialogOpen] = useState(false);

  return (
    <>
      <Stack
        sx={{
          alignItems: "flex-start",
          mb: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          <PerfumeCategoryTitle category={category} quantity={components.length} />
        </Typography>
        {groups.map((group) => (
          <GroupedComposition key={group.key} editor={editor} components={group.components} />
        ))}
        <Stack direction="row" sx={{ flexWrap: "wrap", columnGap: 2 }}>
          <Button
            color="primary"
            size="small"
            variant="text"
            aria-label="add"
            startIcon={<AddIcon />}
            onClick={() => addComponentWithDialog(editor, category)}
          >
            {t("ajouterUneNote")} <PerfumeCategoryTitle category={category} quantity={1} noIcon />
          </Button>
          <Button
            color="primary"
            size="small"
            variant="text"
            startIcon={<AutoAwesomeIcon />}
            disabled={!hasIngredientsToSuggest(editor, category) || isBottleFull(editor)}
            onClick={() => setSuggestionDialogOpen(true)}
          >
            {t("suggestIngredients")}
          </Button>
        </Stack>
      </Stack>
      {suggestionDialogOpen && (
        <IngredientSuggestionDialog
          editor={editor}
          category={category}
          onClose={() => setSuggestionDialogOpen(false)}
        />
      )}
    </>
  );
}

function VolumeInput({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const [draft, setDraft] = useState<string>(String(value));

  return (
    <Input
      type="number"
      inputMode="numeric"
      inputProps={{ min: 0, max: 100 }}
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        const n = parseInt(e.target.value);
        if (!isNaN(n)) onChange(n);
      }}
      onBlur={() => {
        const n = parseInt(draft);
        if (isNaN(n)) {
          onChange(0);
          setDraft("0");
        }
      }}
      // endAdornment={
      //   <InputAdornment position="end">
      //     {(
      //       <IconButton size="small" onClick={() => setDraft("")}>
      //         <CloseIcon fontSize="inherit" />
      //       </IconButton>
      //     )}
      //     ml
      //   </InputAdornment>
      // }
      sx={{ width: "13ch" }}
    />
  );
}

function GroupedComposition({
  editor,
  components,
}: {
  editor: PerfumeEditor;
  components: EditComponentViewModel[];
}) {
  const { t } = useTranslation();
  const ingredientDisplayName = useIngredientDiplayName();

  const firstComponent = components[0];
  const ingredient = firstComponent.ingredient;
  const category = firstComponent.category;
  const availableIngredients = editor.availableIngredients[category];

  const totalVolume = components.reduce((sum, c) => sum + c.volume, 0);

  const ingredientDialogOpen =
    editor.ingredientDialogOpen?.componentId === firstComponent.componentId;

  const openIngredientDialog = () =>
    openComponentIngredientDialog(editor, firstComponent.componentId);
  const [newComponentVolumeAmount, setNewComponentVolumeAmount] = useState("0");
  const newComponentVolumeDialogComponentId = editor.volumeDialogOpen?.componentId;
  const newComponentVolumeDialogOpen =
    newComponentVolumeDialogComponentId !== undefined &&
    components.some((c) => c.componentId === newComponentVolumeDialogComponentId);

  // Track whether ingredient was just selected (to distinguish cancel vs confirm in closeIngredientDialog)
  const ingredientJustSelected = useRef(false);

  const closeIngredientDialog = () => {
    if (!ingredientJustSelected.current && firstComponent.ingredient === null) {
      deleteComponent(editor, firstComponent.componentId);
    }
    ingredientJustSelected.current = false;
    closeComponentIngredientDialog(editor);
  };

  const handleSelectIngredient = (newIngredient: PerfumeIngredient) => {
    const isNew = firstComponent.ingredient === null;
    ingredientJustSelected.current = true;
    components.forEach((c) => editComponent(editor, c.componentId, { ingredient: newIngredient }));
    if (isNew) {
      const alreadyPresent = editor.perfumeViewModel.components.some(
        (c) =>
          c.ingredient?.name === newIngredient.name && c.componentId !== firstComponent.componentId,
      );
      if (alreadyPresent) {
        setNewComponentVolumeAmount("1");
        openComponentVolumeDialog(editor, firstComponent.componentId);
      }
    }
  };

  const [addVolumeDialogOpen, setAddVolumeDialogOpen] = useState(false);
  const [addVolumeAmount, setAddVolumeAmount] = useState("1");

  return (
    <>
      <IngredientDialog
        category={category}
        availableIngredients={availableIngredients}
        selectedIngredient={ingredient}
        onSelectIngredient={handleSelectIngredient}
        open={ingredientDialogOpen}
        onClose={closeIngredientDialog}
      />

      <Card sx={{ display: "flex", mb: 2, width: 1, backgroundColor: "#fafafa" }}>
        {ingredient && (
          <CardMedia
            component="img"
            sx={{ width: 128, objectFit: "contain", cursor: "pointer", mx: 1 }}
            image={`${ingredient.icon}-128.png`}
            onClick={openIngredientDialog}
          />
        )}
        {!ingredient && (
          <Box
            sx={{
              width: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={openIngredientDialog}
          >
            <AddIcon />
          </Box>
        )}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              py: 1,
              "&:last-child": { pb: 1 },
            }}
          >
            <Typography variant="h6">
              {ingredient ? ingredientDisplayName(ingredient) : t("chooseAnIngredient")}
            </Typography>
            <List disablePadding sx={{ borderRadius: 1, mx: -1, maxWidth: "10em" }}>
              {components.map((c) => (
                <ListItem
                  key={c.componentId}
                  disableGutters
                  sx={{ px: 1 }}
                  secondaryAction={
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => deleteComponent(editor, c.componentId)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <VolumeInput
                    value={c.volume}
                    onChange={(n) => editComponent(editor, c.componentId, { volume: n })}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
          <CardActions sx={{ mt: "auto", justifyContent: "space-between" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAddVolumeAmount("1");
                setAddVolumeDialogOpen(true);
              }}
              sx={{ minWidth: "unset", px: 1 }}
            >
              +ml
            </Button>
            {components.length > 1 && <Typography>Total: {totalVolume}&nbsp;ml</Typography>}
          </CardActions>
        </Box>
      </Card>

      <Dialog
        open={newComponentVolumeDialogOpen}
        onClose={() => closeComponentVolumeDialog(editor)}
      >
        {ingredient && (
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IngredientPicture ingredient={ingredient} size="icon" />
            {ingredientDisplayName(ingredient)}
          </DialogTitle>
        )}
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
        >
          <Input
            autoFocus
            endAdornment={<InputAdornment position="end">ml</InputAdornment>}
            type="number"
            inputMode="numeric"
            inputProps={{ min: 0, max: 100 }}
            value={newComponentVolumeAmount}
            onChange={(e) => setNewComponentVolumeAmount(e.target.value)}
            error={
              newComponentVolumeAmount !== "" &&
              (parseInt(newComponentVolumeAmount) < 0 || parseInt(newComponentVolumeAmount) > 100)
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeComponentVolumeDialog(editor)}>{t("cancel")}</Button>
          <Button
            disabled={(() => {
              const n = parseInt(newComponentVolumeAmount);
              return isNaN(n) || n < 0 || n > 100;
            })()}
            onClick={() => {
              editComponent(editor, newComponentVolumeDialogComponentId!, {
                volume: parseInt(newComponentVolumeAmount),
              });
              closeComponentVolumeDialog(editor);
            }}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addVolumeDialogOpen} onClose={() => setAddVolumeDialogOpen(false)}>
        {ingredient && (
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IngredientPicture ingredient={ingredient} size="icon" />
            {ingredientDisplayName(ingredient)}
          </DialogTitle>
        )}
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
        >
          <Input
            autoFocus
            endAdornment={<InputAdornment position="end">ml</InputAdornment>}
            type="number"
            inputMode="numeric"
            value={addVolumeAmount}
            onChange={(e) => setAddVolumeAmount(e.target.value)}
            error={
              addVolumeAmount !== "" &&
              (parseInt(addVolumeAmount) < 0 || parseInt(addVolumeAmount) > 100)
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVolumeDialogOpen(false)}>{t("cancel")}</Button>
          <Button
            disabled={(() => {
              const n = parseInt(addVolumeAmount);
              return isNaN(n) || n < 0 || n > 100;
            })()}
            onClick={() => {
              duplicateComponent(editor, firstComponent.componentId, parseInt(addVolumeAmount));
              setAddVolumeDialogOpen(false);
            }}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PerfumeComposer;
